import { useRegionalFormat } from "@/lib/regional-format/context";
/** QP-172 — Single portfolio row in the Portfolio List. */

import {
  defaultScheduleStartDate,
  parseScheduleDate,
  resolveRecurringScheduleDates,
} from "@repo/shared";
import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useT } from "@/lib/i18n/use-t";
import { formatMonthYear } from "@/lib/utils";
import { T } from "./portfolio-list-row.i18n";

type PortfolioRowProps = {
  portfolio: any;
  showAmount: boolean;
  showFrequency: boolean;
  showDates: boolean;
  language: string;
  onNavigate?: () => void;
};

export function PortfolioRow({
  portfolio,
  showAmount,
  showFrequency,
  showDates,
  language,
  onNavigate,
}: PortfolioRowProps) {
  const t = useT(T);
  const { formatNumber } = useRegionalFormat();
  const isRecurring = portfolio.scheduleType === "recurring";
  const amount = portfolio.amountPerEvent;
  const frequency = portfolio.recurringFrequency;

  const formatFrequency = (freq: string | null): string => {
    if (!freq) return "";
    switch (freq) {
      case "monthly":
        return t.monthly;
      case "quarterly":
        return t.quarterly;
      case "yearly":
        return t.yearly;
      default:
        return freq;
    }
  };

  // Unset dates fall back to the schedule's effective defaults — the same ones
  // the portfolio input widget advertises and the deposit-schedule mapper
  // applies in calculations: start/one-time month → current month, end →
  // start + 10 years.

  // QL-556 — an agreement follow-up keeps the agreement's own dates on the
  // orders (no defaults): show them verbatim, a null end as open-ended.
  const isAgreementFollowup = portfolio.followupSource?.kind === "savingsPlan";
  const { startDate: effectiveStart, endDate: effectiveEnd } = isAgreementFollowup
    ? {
        startDate: parseScheduleDate(portfolio.recurringStartDate),
        endDate: parseScheduleDate(portfolio.recurringEndDate),
      }
    : resolveRecurringScheduleDates(portfolio);
  const effectiveOneTime = parseScheduleDate(portfolio.oneTimeDate) ?? defaultScheduleStartDate();

  const formatMonth = (date: Date): string => formatMonthYear(date, language);

  return (
    <button
      type="button"
      onClick={onNavigate}
      disabled={!onNavigate}
      className="flex items-center justify-between w-full rounded-lg border p-3 text-left hover:bg-muted/50 transition-colors disabled:opacity-60 disabled:cursor-default"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{portfolio.name}</span>
          <Badge variant={isRecurring ? "secondary" : "outline"} className="text-xs shrink-0">
            {isRecurring ? t.recurring : t.oneTime}
          </Badge>
          <Badge variant="outline" className="text-xs shrink-0">
            {portfolio.portfolioType === "custom" ? t.custom : t.model}
          </Badge>
        </div>

        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          {showAmount && amount != null && (
            <span>
              {t.amount}: {formatNumber(amount)}
            </span>
          )}
          {showFrequency && isRecurring && frequency && <span>{formatFrequency(frequency)}</span>}
          {showDates && isRecurring && (
            <>
              {effectiveStart && (
                <span>
                  {t.start}: {formatMonth(effectiveStart)}
                </span>
              )}
              <span>
                {t.end}: {effectiveEnd ? formatMonth(effectiveEnd) : t.openEnded}
              </span>
            </>
          )}
          {showDates && !isRecurring && (
            <span>
              {t.date}: {formatMonth(effectiveOneTime)}
            </span>
          )}
        </div>
      </div>

      {onNavigate && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />}
    </button>
  );
}
